import { Link } from "react-router-dom";
import { ArrowRight, Check, Images } from "lucide-react";
import { InfoPage } from "../components/info/InfoPage";
import { FactorGrid, IllustratedSplit, InfoSection } from "../components/info/InfoSections";
import { CompareTable } from "../components/info/CompareTable";
import { StepTimeline } from "../components/info/StepTimeline";
import { PriceGuide } from "../components/info/PriceGuide";
import { TextBody } from "../components/info/TextBody";
import { CompareLink } from "../components/info/InfoHero";
import { ProfileSystemsSection } from "../components/catalog/ProfileSystemsSection";
import { QuoteButton } from "../components/home/CtaButtons";
import { buildServiceJsonLd } from "../lib/seo";
import { PUIEN_NAV, PUIEN_POINTS, PUI_BADGE, PUI_COST_FACTORS, PUI_GLASS_TABLE, PUI_GLASS_TEXT, PUI_MATERIAL_TABLE, PUI_MATERIAL_TEXT,
  PUI_MONTAGE_TEXT, PUI_STEPS, PUI_TECH, PUI_TYPES } from "../data/info/grotepuien";

const ISDE = "Vervangt de nieuwe pui een bestaande pui, raam of deur? Dan kan onder voorwaarden ISDE-subsidie gelden voor het isolatieglas. Bij een pui in een nieuwe opening, zoals een doorbraak of nieuwe aanbouw, geldt dat niet.";
const PUI_KEYS = ["kunststof_schuifpui", "aluminium_schuifpui", "aluminium_hefschuifpui"];

const TypeCards = () => (
  <ul className="grid gap-5 md:grid-cols-2" data-testid="puien-types">
    {PUI_TYPES.map((t) => (
      <li key={t.title} className="card card-hover flex flex-col p-6 sm:p-7" data-testid={`puien-type-${t.href.slice(1)}`}>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{t.tag}</p>
        <h3 className="mt-2 text-[20px] font-semibold text-ink">{t.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{t.text}</p>
        <ul className="mt-4 grid flex-1 gap-1.5">
          {t.fit.map((f) => <li key={f} className="flex items-start gap-2 text-[14.5px] text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-maas" aria-hidden="true" />{f}</li>)}
        </ul>
        <Link to={t.href} className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline">Meer over {t.title.toLowerCase()} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </li>
    ))}
  </ul>
);

const InspirationCard = () => (
  <Link to="/projecten/inspiratie" className="card card-hover group mt-6 flex items-center gap-4 p-5" data-testid="puien-inspiration-link">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-maas-soft text-maas"><Images className="h-5 w-5" aria-hidden="true" /></span>
    <span className="flex-1"><span className="block text-[16px] font-semibold text-ink group-hover:text-maas">Inspiratie voor puien en schuifpuien</span>
      <span className="block text-[14px] text-slate-600">Bekijk uitvoeringen in kunststof en aluminium, en vraag een vergelijkbare pui aan.</span></span>
    <ArrowRight className="h-5 w-5 text-maas transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
  </Link>
);

export default function GrotePuien() {
  return (
    <InfoPage page="grotepuien" label="Grote puien" eyebrow="Grote puien" points={PUIEN_POINTS} mediaKey="ka_grotepuien_hero" nav={PUIEN_NAV}
      onderwerp="aluminium-hefschuifpui" faqTitle="Vragen over grote puien" endTitle="Een grote pui op uw wensenlijst?" isdeText={ISDE} badge={PUI_BADGE}
      kbCategory="schuifpuien" linkHrefs={["/hr++-glas-isolatieglas", "/montage", "/kosten-kunststof-kozijnen-zuid-holland"]}
      jsonLd={buildServiceJsonLd("Grote puien, schuifpuien en hefschuifpuien", "Advies, inmeten, levering, montage en afwerking van grote kunststof en aluminium puien, schuifpuien en hefschuifpuien in Zuid-Holland.")}>
      <InfoSection id="soorten" eyebrow="Soorten puien" title="Welke grote pui past bij uw opening?"
        lead="Een grote pui is er in verschillende uitvoeringen. Welke het beste past, hangt af van de breedte, hoe u de pui wilt gebruiken en de uitstraling van uw woning.">
        <TypeCards />
        <InspirationCard />
      </InfoSection>
      <InfoSection id="materiaal" tone="slate" eyebrow="Materiaal" title="Een grote pui in kunststof of aluminium">
        <IllustratedSplit mediaKey="ka_grotepuien_soorten" text={PUI_MATERIAL_TEXT} testid="puien-material-split" />
        <div className="mt-12"><CompareTable body={PUI_MATERIAL_TABLE} caption="Grote puien in kunststof en aluminium vergeleken" testid="puien-material-table" /></div>
      </InfoSection>
      <InfoSection id="techniek" eyebrow="Technische mogelijkheden" title="Waar we op letten bij een grote pui"
        lead="Bij brede puien kijken we verder dan het kozijn zelf: naar het gewicht van het glas, de dorpel, de bediening, de veiligheid en de constructie.">
        <FactorGrid items={PUI_TECH} testid="puien-tech" />
      </InfoSection>
      <ProfileSystemsSection productKeys={PUI_KEYS} onderwerp="aluminium-hefschuifpui"
        lead="Deze profielsystemen voor schuif- en hefschuifpuien kunt u bij ons kiezen. De technische gegevens komen van de fabrikant; de waarden voor uw pui hangen af van de maten en het glas." />
      <InfoSection id="glas" tone="slate" eyebrow="Glaskeuze" title="Het juiste glas voor een groot glasvlak">
        <IllustratedSplit mediaKey="ka_grotepuien_glas" text={PUI_GLASS_TEXT} reverse testid="puien-glass-split" />
        <div className="mt-12"><CompareTable body={PUI_GLASS_TABLE} caption="Glassoorten voor grote puien" testid="puien-glass-table" /></div>
      </InfoSection>
      <InfoSection id="montage" eyebrow="Montage & afwerking" title="Zo plaatsen wij een grote pui"
        lead="Van inmeten tot oplevering: bij een grote pui is de voorbereiding minstens zo belangrijk als de montage zelf.">
        <IllustratedSplit mediaKey="ka_grotepuien_montage" text={PUI_MONTAGE_TEXT} testid="puien-montage-split" />
        <div className="mt-12"><StepTimeline steps={PUI_STEPS} testid="puien-timeline" /></div>
      </InfoSection>
      <InfoSection id="kosten" tone="slate" eyebrow="Kosten" title="Wat kost een grote pui?"
        lead="Wij tonen alleen richtprijzen die wij zelf hebben gecontroleerd en goedgekeurd. Uw definitieve prijs volgt na het inmeten.">
        <PriceGuide keys={PUI_KEYS} />
        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <TextBody text={PUI_COST_FACTORS} className="lg:col-span-7" testid="puien-cost-factors" />
          <div className="card flex flex-col gap-4 p-6 lg:col-span-5" data-testid="puien-cost-card">
            <p className="text-[18px] font-semibold text-ink">Een prijs voor uw opening</p>
            <p className="text-[15px] leading-relaxed text-slate-600">Een foto en globale maten zijn genoeg voor een eerste persoonlijke prijsindicatie.</p>
            <QuoteButton location="puien-cost" onderwerp="aluminium-hefschuifpui" label="Vraag een prijsindicatie aan" />
            <CompareLink location="puien-cost" label="Heeft u al een offerte? Vergelijk uw offerte" />
          </div>
        </div>
      </InfoSection>
    </InfoPage>
  );
}
